import React from 'react';

import {
  HeaderBiddersAPIKey,
  IHeaderBidderListInfo,
} from '~/src/dash/api/headerbidders/headerbidders_models';
import { EBool } from '~/src/dash/api/types/bool';
import {
  formatSelectedAllActiveInactiveToEBoolString,
  formatSelectedBidderTiers,
  formatSelectedToArrayOfNumbers,
  formatSelectedToArrayOfStrings,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import {
  BidderLabelRender,
  CurrencyRender,
  DateTimeRender,
  EllipsisWithTooltipRender,
  PercentTrendRender,
  PrioritySelectRender,
  SuperUserOnlySwitchRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { Div, Text } from '~/src/dash/publicaUI';

export const formatBiddersResponseIntoLVTRows = (
  bidders: IHeaderBidderListInfo[],
  cfg: IBiddersListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  bidders.forEach((bidder) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = bidder[responseKey as keyof IHeaderBidderListInfo];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

interface IFloatNull {
  Float64: number;
  Valid: boolean;
}

const formatFloatNull = (value: IFloatNull) => {
  if (value && value.Valid) {
    return value.Float64;
  }
  return null;
};

const ChannelsListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnChannelsList(props.row.ID.value)}
      style={{ cursor: 'pointer' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

const ChannelGroupsListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnChannelGroupsList(props.row.ID.value)}
      style={{ cursor: 'pointer' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

interface IBiddersListViewTable {
  columns: IListViewTableColumns;
}

export interface IBiddersListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: HeaderBiddersAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: HeaderBiddersAPIKey[]; // what keys should be made active on reset
  };
  filters: ICustomAPIFilterConfig;
  isInitialized?: boolean;
  sorts: HeaderBiddersAPIKey[];
  trends: HeaderBiddersAPIKey[];
  table: IBiddersListViewTable;
  tabKey: string;
}

export const biddersListViewConfig = {
  cypressClassName: 'bidderslistview',
  tabKey: 'All',
  defaultState: {
    activeColumnKeys: [
      'SuperOnly',
      'Bidder',
      'Priority',
      'SiteIDS',
      'ChannelGroupIDS',
      'PastWeekRevenue',
    ],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'past_week_revenue',
    page: 0,
    rowsPerPage: 25,
  },
  columnSelector: {
    resetKeys: ['SuperOnly', 'Bidder', 'Priority', 'SiteIDS', 'ChannelGroupIDS', 'PastWeekRevenue'],
  },
  filters: {
    Channels: {
      queryKey: 'selectedChannelIDs',
      formatRequestValue: formatSelectedToArrayOfNumbers,
      hasSelectAll: true,
    },
    'Filtered Demand Sources': {
      queryKey: 'demandSources',
      formatRequestValue: formatSelectedToArrayOfStrings,
      isMultiSelectDisabled: true,
    },
    'Bidder Groups': {
      queryKey: 'bidderGroupIDs',
      formatRequestValue: formatSelectedToArrayOfNumbers,
      hasSelectAll: true,
    },
    'Bidder Labels': {
      queryKey: 'bidderLabels',
      formatRequestValue: formatSelectedToArrayOfStrings,
      hasSelectAll: true,
    },
    'Bidder Tiers': {
      queryKey: 'selectedPriorities',
      formatRequestValue: formatSelectedBidderTiers,
      hasSelectAll: true,
    },
    Status: {
      defaultSelected: ['1'], // Active
      queryKey: 'activeBidderFilter',
      formatRequestValue: formatSelectedAllActiveInactiveToEBoolString,
    },
  },
  sorts: ['CreatedAt', 'UpdatedAt', 'PastWeekRevenue', 'PastWeekImpressions'],
  trends: ['PastWeekRevenueTrend', 'PastWeekImpressionsTrend'],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Status: {
        defaultOrderDirection: 'desc',
        formatValue: (value: number) => value === EBool.EBool_TRUE,
        headerRenderType: 'switch',
        key: 'Status',
        label: 'Status',
        requestKey: 'active',
        responseKey: 'Active',
        valueType: 'boolean',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
      },
      BidderLabelsArray: {
        cellJSX: BidderLabelRender,
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        isSearchable: true,
        key: 'BidderLabelsArray',
        label: 'Bidder Label',
        requestKey: 'bidder_label_array',
        responseKey: 'BidderLabelsArray',
        valueType: 'array',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      Bidder: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        isDefaultActive: true,
        isSearchable: true,
        key: 'Bidder',
        label: 'Demand Source',
        orderByKey: 'demand_source_ui_to_db_name.ui_name',
        requestKey: 'demand_sources',
        responseKey: 'Bidder',
        valueType: 'string',
      },
      Priority: {
        cellJSX: PrioritySelectRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number_select',
        isDefaultActive: true,
        key: 'Priority',
        label: 'Tier',
        orderByKey: 'priority',
        requestKey: 'priority',
        responseKey: 'Priority',
        valueType: 'number',
      },
      PastWeekRevenue: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Last 7 Days',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekRevenue',
        label: 'Rev. (L7D)',
        orderByKey: 'past_week_revenue',
        requestKey: '',
        responseKey: 'PastWeekRevenue',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Revenue',
        trendKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      PastWeekRevenueTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: formatFloatNull,
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Trend L7D Compared to Previous Period',
        isDefaultActive: true,
        key: 'PastWeekRevenueTrend',
        label: 'Rev. Trend',
        orderByKey: 'past_week_revenue_trend',
        parentKey: 'PastWeekRevenue',
        requestKey: '',
        responseKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      PastWeekImpressions: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Last 7 Days',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekImpressions',
        label: 'Imp. (L7D)',
        orderByKey: 'past_week_impressions',
        requestKey: '',
        responseKey: 'PastWeekImpressions',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Impressions',
        trendKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      PastWeekImpressionsTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: formatFloatNull,
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Trend L7D Compared to Previous Period',
        isDefaultActive: true,
        key: 'PastWeekImpressionsTrend',
        label: 'Imp. Trend',
        orderByKey: 'past_week_impressions_trend',
        parentKey: 'PastWeekImpressions',
        requestKey: '',
        responseKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      SiteIDS: {
        cellJSX: ChannelsListModalLink,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: (value: number[]) => (value || []).length,
        headerRenderType: 'number_small',
        isDefaultActive: true,
        key: 'SiteIDS',
        label: 'Targeted Channels',
        orderByKey: 'targeted_channels',
        requestKey: '',
        responseKey: 'SiteIDS',
        valueType: 'array',
      },
      ChannelGroupIDS: {
        cellJSX: ChannelGroupsListModalLink,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: (value: number[]) => (value || []).length,
        headerRenderType: 'number',
        isDefaultActive: true,
        key: 'ChannelGroupIDS',
        label: 'Targeted Channel Groups',
        orderByKey: 'targeted_channel_groups',
        requestKey: '',
        responseKey: 'ChannelGroupIDS',
        valueType: 'array',
      },
      CreatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'CreatedAt',
        label: 'Created Date',
        orderByKey: 'created_at',
        requestKey: '',
        responseKey: 'CreatedAt',
        sortLabel: 'Last Created',
        valueType: 'date',
      },
      UpdatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'UpdatedAt',
        label: 'Last Updated',
        orderByKey: 'updated_At',
        requestKey: '',
        responseKey: 'UpdatedAt',
        sortLabel: 'Last Updated',
        valueType: 'date',
      },
      SuperOnly: {
        cellJSX: SuperUserOnlySwitchRender,
        cellRenderType: 'simple_switch',
        defaultOrderDirection: 'desc',
        headerRenderType: 'simple_switch',
        isSuperUserOnly: true,
        key: 'SuperOnly',
        label: 'Super-User Only *',
        orderByKey: 'super_only',
        requestKey: 'super_only',
        responseKey: 'SuperOnly',
        valueType: 'boolean',
      },
      FlexibleBiddingEnabled: {
        formatValue: (value: number) => value === 2,
        isHidden: true,
        key: 'FlexibleBiddingEnabled',
        label: 'Flexible Bidding Enabled',
        requestKey: '',
        responseKey: 'FlexibleBiddingEnabled',
        valueType: 'boolean',
      },
      FlexibleBiddingThresholds: {
        isHidden: true,
        key: 'FlexibleBiddingThresholds',
        label: 'Flexible Bidding Thresholds',
        requestKey: '',
        responseKey: 'FlexibleBiddingThresholds',
        valueType: 'array',
      },
    },
  },
} as IBiddersListViewConfig;
